import {FC, useEffect, useMemo, useState} from "react";
import {Colors, useBreakpoint} from "@brg/ui";
import {LayoutConst} from "../../../../../../core";
import {useTranslation} from "react-i18next";
import styled from "styled-components";

const CourseInfoHeaderBlock = styled.div`
  display: flex;
  justify-content: center;
  border-top: 1px solid ${Colors.galBlack31};
  background: ${Colors.geoWhite100};
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.12);
  padding: 8px 0;
  width: 100%;

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 6px 0;
  }
`

const CourseInfoTitleBlock = styled.div`
  width: 1152px;
  color: ${Colors.galBlack100};
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 17px;
  font-style: normal;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: -0.02em;
  text-wrap: nowrap;
  
  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    width: 888px;
    font-size: 15px;
    line-height: 20px;
  }

  @media (max-width: ${LayoutConst.screen.md}px) {
    width: calc(100% - 32px);
  }
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    width: calc(100% - 20px);
    font-size: 13px;
    line-height: 16px;
  }

  &::-webkit-scrollbar {
    height: 0px;
    width: 0px;
  }
  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.app?.backgroundColor === '#FEFEFE' ? `${Colors.geoWhite47}` : `${Colors.geoWhite100}`};
  }
`

const getBlocksByStyle = (longreadList: any, style: string) => {
    const themesList: any = []
    longreadList.map((longread: any) =>
        longread
            ?.filter((block: any) => block?.style === style)
            ?.forEach((block: any, index: number) => themesList.push({...block, lessonIndex: index}))
    )
    return themesList
}

const getThemesList = (longreadList: any) => {
    const blocks = getBlocksByStyle(longreadList, 'course_theme')

    return blocks.map((block: any) => ({
        block,
        ref: document.querySelector(`[data-rbd-draggable-id="${block?.postId}"]`)
    }))
}

const getLessonsList = (longreadList: any) => {
    const blocks = getBlocksByStyle(longreadList, 'course_lesson')

    return blocks.map((block: any) => ({
        block: {
            ...block,
            text: document
                .getElementById(`lesson-title-${block?.text}`)
                ?.getAttribute('data-title') || ""
        },
        ref: document.querySelector(`[data-rbd-draggable-id="${block?.postId}"]`)
    }))
}

export const CourseInfoHeader: FC<{headerThemeAnchors: any, themesCount?: number, hasExam?: boolean}> = ({headerThemeAnchors, themesCount = 0, hasExam = false}) => {

    const is744 = useBreakpoint(LayoutConst.screen.s)
    const { t } = useTranslation()

    const [themeTitle, setThemeTitle] = useState({ text: '', index: -1 })
    const [lessonTitle, setLessonTitle] = useState({ text: '', index: -1 })

    const offsetTop = 80 

    const getIsReached = (ref: any) => {
        if (!ref) return false
        const topPos = ref?.getBoundingClientRect()?.top + window.pageYOffset
        return window.pageYOffset >= topPos - offsetTop
    }

    const handleScroll = () => {
        const themesList = getThemesList(Object.values(headerThemeAnchors))
            .map((theme: any) => ({
                ...theme,
                top: theme?.ref?.getBoundingClientRect()?.top + window.pageYOffset
            }))
            .sort((a: any, b: any) => a.top - b.top)
    
        const lessonList = getLessonsList(Object.values(headerThemeAnchors))
            .map((lesson: any) => ({
                ...lesson,
                top: lesson?.ref?.getBoundingClientRect()?.top + window.pageYOffset
            }))
            .sort((a: any, b: any) => a.top - b.top)

        themesList.forEach((theme: any, idx: number) => {
        })
    
        lessonList.forEach((lesson: any, idx: number) => {
        })
    
        let activeTheme = { text: '', index: -1 }
        themesList.forEach((theme: any, idx: number) => {
            if (window.scrollY >= theme.top - offsetTop) {
                activeTheme = { text: theme?.block?.text || '', index: idx + 1 }
            }
        })
    
        let activeLesson = { text: '', index: -1 }
        lessonList.forEach((lesson: any, idx: number) => {
            if (window.scrollY >= lesson.top - offsetTop && lesson?.block?.text) {
                activeLesson = { text: lesson?.block?.text || '', index: lesson?.block?.lessonIndex + 1 }
            }
        })
    
        setThemeTitle(activeTheme)
        setLessonTitle(activeLesson)
    }

    useEffect(() => {
        window.addEventListener('scroll', handleScroll)

        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [headerThemeAnchors])

    useEffect(() => {
        handleScroll()
    }, [headerThemeAnchors])

    const preparedThemeTitle = useMemo(
        () => {
            if (themeTitle?.index === -1) return ''
            const isExam = hasExam && themesCount > 0 && themeTitle?.index > themesCount
            
            if (is744) {
                return isExam 
                    ? `${t('project_edit.lms.exam')}. ${themeTitle?.text}`
                    : themeTitle?.text
            }
            return isExam 
                ? `${t('project_edit.lms.exam')}. ${themeTitle?.text}`
                : t('project_edit.lms.theme_name', {order: themeTitle?.index, name: themeTitle?.text})
        },
        [t, is744, themeTitle, hasExam, themesCount]
    )

    const preparedLessonTitle = useMemo(
        () =>
            lessonTitle?.index === -1
                ? ''
                : is744
                    ? lessonTitle?.text
                    : t('project_edit.lms.lesson_name', {order: lessonTitle?.index, name: lessonTitle?.text}),
        [t, is744, themeTitle, lessonTitle]
    )

    const resultTitle = useMemo(
        () =>
            !preparedThemeTitle && !preparedLessonTitle
                ? ''
                : !preparedLessonTitle
                    ? preparedThemeTitle
                    :`${preparedThemeTitle} | ${preparedLessonTitle}`,
        [preparedThemeTitle, preparedLessonTitle]
    )

    if (!resultTitle) return null;
    return <CourseInfoHeaderBlock><CourseInfoTitleBlock>{resultTitle}</CourseInfoTitleBlock></CourseInfoHeaderBlock>
}import {FC, useEffect, useMemo, useState} from "react";
import {Colors, useBreakpoint} from "@brg/ui";
import {LayoutConst} from "../../../../../../core";
import {useTranslation} from "react-i18next";
import styled from "styled-components";

const CourseInfoHeaderBlock = styled.div`
  display: flex;
  justify-content: center;
  border-top: 1px solid ${Colors.galBlack31};
  background: ${Colors.geoWhite100};
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.12);
  padding: 8px 0;
  width: 100%;

  @media (max-width: ${LayoutConst.screen.s}px) {
    padding: 6px 0;
  }
`

const CourseInfoTitleBlock = styled.div`
  width: 1152px;
  color: ${Colors.galBlack100};
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 17px;
  font-style: normal;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: -0.02em;
  text-wrap: nowrap;
  
  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    width: 888px;
    font-size: 15px;
    line-height: 20px;
  }

  @media (max-width: ${LayoutConst.screen.md}px) {
    width: calc(100% - 32px);
  }
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    width: calc(100% - 20px);
    font-size: 13px;
    line-height: 16px;
  }

  &::-webkit-scrollbar {
    height: 0px;
    width: 0px;
  }
  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background-color: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.app?.backgroundColor === '#FEFEFE' ? `${Colors.geoWhite47}` : `${Colors.geoWhite100}`};
  }
`

const getBlocksByStyle = (longreadList: any, style: string) => {
    const themesList: any = []
    longreadList.map((longread: any) =>
        longread
            ?.filter((block: any) => block?.style === style)
            ?.forEach((block: any, index: number) => themesList.push({...block, lessonIndex: index}))
    )
    return themesList
}

const getThemesList = (longreadList: any) => {
    const blocks = getBlocksByStyle(longreadList, 'course_theme')

    return blocks.map((block: any) => ({
        block,
        ref: document.querySelector(`[data-rbd-draggable-id="${block?.postId}"]`)
    }))
}

const getLessonsList = (longreadList: any) => {
    const blocks = getBlocksByStyle(longreadList, 'course_lesson')

    return blocks.map((block: any) => ({
        block: {
            ...block,
            text: document
                .getElementById(`lesson-title-${block?.text}`)
                ?.getAttribute('data-title') || ""
        },
        ref: document.querySelector(`[data-rbd-draggable-id="${block?.postId}"]`)
    }))
}

export const CourseInfoHeader: FC<{headerThemeAnchors: any, themesCount?: number, hasExam?: boolean}> = ({headerThemeAnchors, themesCount = 0, hasExam = false}) => {

    const is744 = useBreakpoint(LayoutConst.screen.s)
    const { t } = useTranslation()

    const [themeTitle, setThemeTitle] = useState({ text: '', index: -1 })
    const [lessonTitle, setLessonTitle] = useState({ text: '', index: -1 })

    const offsetTop = 80 

    const getIsReached = (ref: any) => {
        if (!ref) return false
        const topPos = ref?.getBoundingClientRect()?.top + window.pageYOffset
        return window.pageYOffset >= topPos - offsetTop
    }

    const handleScroll = () => {
        const themesList = getThemesList(Object.values(headerThemeAnchors))
            .map((theme: any) => ({
                ...theme,
                top: theme?.ref?.getBoundingClientRect()?.top + window.pageYOffset
            }))
            .sort((a: any, b: any) => a.top - b.top)
    
        const lessonList = getLessonsList(Object.values(headerThemeAnchors))
            .map((lesson: any) => ({
                ...lesson,
                top: lesson?.ref?.getBoundingClientRect()?.top + window.pageYOffset
            }))
            .sort((a: any, b: any) => a.top - b.top)

        themesList.forEach((theme: any, idx: number) => {
        })
    
        lessonList.forEach((lesson: any, idx: number) => {
        })
    
        let activeTheme = { text: '', index: -1 }
        themesList.forEach((theme: any, idx: number) => {
            if (window.scrollY >= theme.top - offsetTop) {
                activeTheme = { text: theme?.block?.text || '', index: idx + 1 }
            }
        })
    
        let activeLesson = { text: '', index: -1 }
        lessonList.forEach((lesson: any, idx: number) => {
            if (window.scrollY >= lesson.top - offsetTop && lesson?.block?.text) {
                activeLesson = { text: lesson?.block?.text || '', index: lesson?.block?.lessonIndex + 1 }
            }
        })
    
        setThemeTitle(activeTheme)
        setLessonTitle(activeLesson)
    }

    useEffect(() => {
        window.addEventListener('scroll', handleScroll)

        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [headerThemeAnchors])

    useEffect(() => {
        handleScroll()
    }, [headerThemeAnchors])

    const preparedThemeTitle = useMemo(
        () => {
            if (themeTitle?.index === -1) return ''
            const isExam = hasExam && themesCount > 0 && themeTitle?.index > themesCount
            
            if (is744) {
                return isExam 
                    ? `${t('project_edit.lms.exam')}. ${themeTitle?.text}`
                    : themeTitle?.text
            }
            return isExam 
                ? `${t('project_edit.lms.exam')}. ${themeTitle?.text}`
                : t('project_edit.lms.theme_name', {order: themeTitle?.index, name: themeTitle?.text})
        },
        [t, is744, themeTitle, hasExam, themesCount]
    )

    const preparedLessonTitle = useMemo(
        () =>
            lessonTitle?.index === -1
                ? ''
                : is744
                    ? lessonTitle?.text
                    : t('project_edit.lms.lesson_name', {order: lessonTitle?.index, name: lessonTitle?.text}),
        [t, is744, themeTitle, lessonTitle]
    )

    const resultTitle = useMemo(
        () =>
            !preparedThemeTitle && !preparedLessonTitle
                ? ''
                : !preparedLessonTitle
                    ? preparedThemeTitle
                    :`${preparedThemeTitle} | ${preparedLessonTitle}`,
        [preparedThemeTitle, preparedLessonTitle]
    )

    if (!resultTitle) return null;
    return <CourseInfoHeaderBlock><CourseInfoTitleBlock>{resultTitle}</CourseInfoTitleBlock></CourseInfoHeaderBlock>
}